import { useEffect, useMemo } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { SearchInput } from '@aimsos/ui';
import { useDashboardData } from '../context';
import {
  FILTER_BAR_BUTTON_CLASSNAME,
  FILTER_BAR_SEARCH_INPUT_CLASSNAME,
} from '../constants';
import DashboardButton from './DashboardButton';
import FilterDropdown from './FilterDropdown';
import { useTemplateFilters } from '../hooks/useTemplateFilters';

const TemplateFilters = () => {
  const { t } = useTranslation('dashboards');
  const {
    sectionFilters, templateCategories, fetchSectionFilters, fetchTemplateCategories,
  } = useDashboardData();
  const {
    localSearch,
    setLocalSearch,
    templateFilters,
    handleSectionChange,
    handleCategoryChange,
    resetFilters,
    hasActiveFilters,
  } = useTemplateFilters();

  useEffect(() => {
    fetchSectionFilters();
  }, [fetchSectionFilters]);

  useEffect(() => {
    fetchTemplateCategories();
  }, [fetchTemplateCategories]);

  const sectionLabel = useMemo(() => {
    if (!templateFilters.sectionId) return t('filters.sections');
    const section = sectionFilters.find((item) => item.id === templateFilters.sectionId);
    return section?.path || section?.name || t('filters.sections');
  }, [sectionFilters, t, templateFilters.sectionId]);

  const categoryLabel = useMemo(() => {
    if (!templateFilters.categoryId) return t('filters.categories');
    const category = templateCategories.find((item) => item.id === templateFilters.categoryId);
    return category?.name || t('filters.categories');
  }, [t, templateCategories, templateFilters.categoryId]);

  const categoryOptions = useMemo(
    () => templateCategories.map((category) => ({ value: category.id, label: category.name })),
    [templateCategories],
  );

  const sectionOptions = useMemo(
    () => sectionFilters.map((section) => ({ value: section.id, label: section.path || section.name })),
    [sectionFilters],
  );

  return (
    <section>
      <div className="flex flex-wrap items-center gap-3">
        <div className="w-full max-w-xs">
          <SearchInput
            className={FILTER_BAR_SEARCH_INPUT_CLASSNAME}
            value={localSearch}
            onChange={(event) => setLocalSearch(event.target.value)}
            onClear={() => setLocalSearch('')}
            placeholder={t('filters.search')}
          />
        </div>

        <FilterDropdown label={categoryLabel} options={categoryOptions} onSelect={handleCategoryChange} />
        <FilterDropdown
          label={sectionLabel}
          options={sectionOptions}
          onSelect={handleSectionChange}
          className="max-w-72"
        />

        {hasActiveFilters && (
          <DashboardButton variant="secondary" onClick={resetFilters} className={FILTER_BAR_BUTTON_CLASSNAME}>
            {t('filters.clear')}
          </DashboardButton>
        )}
      </div>
    </section>
  );
};

export default TemplateFilters;
